// src/pages/Welcome.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as apiService from '../services/api';
import Swal from 'sweetalert2';
import './Welcome.css';
import '../styles/animations.css';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle } from '@fortawesome/free-regular-svg-icons';
import AIGuide from '../components/AIGuide';
import AIGuideTest from '../components/AIGuideTest';

const Welcome = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // State for flux and providers data
  const [fluxes, setFluxes] = useState([]);
  const [providers, setProviders] = useState([]);
  
  // State initialization for wizard
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedFlux: null,
    selectedInputProviders: [],
    selectedActionProviders: {},  // Changed to object with provider_action as key
    selectedOutputProviders: [],
    providerCredentials: {},
    options: {}, // Add options field to store selected option values
  });
  
  // Force AI Guide to show initially for testing
  const [showAIGuide, setShowAIGuide] = useState(true);
  
  // Group action providers by provider_action
  const groupActionProviders = (actionProviders) => {
    const groups = {};
    
    actionProviders.forEach(providerId => {
      const provider = providers.find(p => p.id === providerId.id);
      if (provider && provider.provider_action) {
        if (!groups[provider.provider_action]) {
          groups[provider.provider_action] = [];
        }
        groups[provider.provider_action].push(provider);
      }
    });
    
    return groups;
  };
  
  // Load data from localStorage on component mount
  useEffect(() => {
    try {
      // Load providers
      const providersData = localStorage.getItem('providers');
      if (providersData) {
        const parsedProviders = JSON.parse(providersData);
        console.log('Loaded providers:', parsedProviders);
        setProviders(parsedProviders);
      }
      
      // Load fluxes
      const fluxesData = localStorage.getItem('templates');
      if (fluxesData) {
        const parsedFluxes = JSON.parse(fluxesData);
        setFluxes(parsedFluxes);
      }
    } catch (error) {
      console.error('Error loading data from localStorage:', error);
      showErrorAlert('Error al cargar datos', 'Hubo un problema al cargar los datos necesarios.');
    }
  }, []);
  
  // Handle flux selection
  const handleFluxSelection = (flux) => {
    const defaultOptions = {};
    
    // Initialize options with default values if flux has options
    if (flux.options && flux.options.length > 0) {
      flux.options.forEach(option => {
        if (option.default_value) {
          // Convert to appropriate type
          if (option.option_type === 'boolean') {
            defaultOptions[option.id] = option.default_value.toLowerCase() === 'true';
          } else if (option.option_type === 'number') {
            defaultOptions[option.id] = parseFloat(option.default_value);
          } else {
            defaultOptions[option.id] = option.default_value;
          }
        }
      });
    }
    
    // Initialize empty selectedActionProviders object based on available action types
    const actionProviderGroups = groupActionProviders(flux.action_provider_ids || []);
    const initialSelectedActions = {};
    Object.keys(actionProviderGroups).forEach(actionType => {
      initialSelectedActions[actionType] = null; // null means "No ejecutar esta acción"
    });
    
    setFormData(prev => ({
      ...prev,
      selectedFlux: flux,
      name: `${flux.name} Bot`,
      description: flux.description || `Bot based on ${flux.name} flux`,
      options: defaultOptions,
      selectedActionProviders: initialSelectedActions
    }));
  };
  
  // Handle provider selection
  const handleProviderSelection = (providerId, category) => {
    setFormData(prev => {
      // For input and output providers - same as before
      if (category === 'Input' || category === 'Output') {
        const categoryKey = `selected${category}Providers`;
        const currentSelections = [...prev[categoryKey]];
        
        // If clicking on an already selected provider, deselect it
        if (currentSelections.includes(providerId)) {
          return {
            ...prev,
            [categoryKey]: []
          };
        } 
        // Otherwise, replace any existing selection with the new one
        else {
          return {
            ...prev,
            [categoryKey]: [providerId]
          };
        }
      } 
      // For action providers - set the selected provider for its action type
      else if (category === 'Action') {
        const provider = providers.find(p => p.id === providerId);
        if (!provider || !provider.provider_action) return prev;
        
        const actionType = provider.provider_action;
        const currentProvider = prev.selectedActionProviders[actionType];
        
        // Toggle selection: if same provider is selected, set to null (none)
        const newSelectedProvider = currentProvider === providerId ? null : providerId;
        
        return {
          ...prev,
          selectedActionProviders: {
            ...prev.selectedActionProviders,
            [actionType]: newSelectedProvider
          }
        };
      }
      
      return prev;
    });
  };
  
  // Get all selected provider IDs across all categories (input, action, output)
  const getAllSelectedProviderIds = () => {
    const actionProviderIds = Object.values(formData.selectedActionProviders)
      .filter(id => id !== null); // Filter out null values (no action)
    
    const allIds = [
      ...formData.selectedInputProviders,
      ...actionProviderIds,
      ...formData.selectedOutputProviders
    ];
    // Remove duplicates
    return [...new Set(allIds)];
  };
  
  // Handle credential changes for providers
  const handleCredentialChange = (providerId, field, value) => {
    setFormData(prev => {
      const currentCredentials = prev.providerCredentials || {};
      const providerCredentials = currentCredentials[providerId] || {};
      
      return {
        ...prev,
        providerCredentials: {
          ...currentCredentials,
          [providerId]: {
            ...providerCredentials,
            [field]: value
          }
        }
      };
    });
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle option value change
  const handleOptionChange = (optionId, value) => {
    setFormData(prev => ({
      ...prev,
      options: {
        ...prev.options,
        [optionId]: value
      }
    }));
  };
  
  // Next Step validation
  const nextStep = () => {
    const { name, description, selectedInputProviders, selectedActionProviders, selectedOutputProviders, providerCredentials } = formData;
    
    // Validations based on current step
    if (currentStep === 1) {
      if (!formData.selectedFlux) {
        showErrorAlert('Selección requerida', 'Por favor selecciona un flujo para tu bot.');
        return false;
      }
      
      setCurrentStep(2);
      return true;
    }
    
    if (currentStep === 2) {
      // Validate provider selections
      
      // Only validate if the flux actually has these provider types
      if (formData.selectedFlux) {
        if (formData.selectedFlux.input_provider_ids?.length > 0 && selectedInputProviders.length === 0) {
          showErrorAlert('Selección requerida', 'Por favor selecciona al menos un proveedor de entrada.');
          return false;
        }
        
        // No validation required for action providers - user can select "No ejecutar" for all
        
        if (formData.selectedFlux.output_provider_ids?.length > 0 && selectedOutputProviders.length === 0) {
          showErrorAlert('Selección requerida', 'Por favor selecciona al menos un proveedor de salida.');
          return false;
        }
      }
      
      setCurrentStep(3);
      return true;
    }
    
    if (currentStep === 3) {
      if (!name.trim()) {
        showErrorAlert('Nombre requerido', 'Por favor ingresa un nombre para tu bot.');
        return false;
      }
      
      if (!description.trim()) {
        showErrorAlert('Descripción requerida', 'Por favor ingresa una descripción para tu bot.');
        return false;
      }
      
      setCurrentStep(4);
      return true;
    }
    
    if (currentStep === 4) {
      // Validate provider credentials
      const selectedProviderIds = getAllSelectedProviderIds();
      
      if (selectedProviderIds.length === 0) {
        showErrorAlert('Proveedores requeridos', 'Por favor selecciona al menos un proveedor.');
        return false;
      }
      
      const missingCredentials = [];
      
      selectedProviderIds.forEach(providerId => {
        const provider = providers.find(p => p.id === providerId);
        if (provider) {
          const credentialFields = getCredentialFieldsFromTemplate(provider);
          
          // Check only required fields
          credentialFields.filter(field => field.required).forEach(field => {
            const fieldValue = providerCredentials[providerId]?.[field.key];
            if (!fieldValue && fieldValue !== 0) {
              missingCredentials.push(`${provider.name} (${field.label})`);
            }
          });
        }
      });
      
      if (missingCredentials.length > 0) {
        showErrorAlert(
          'Credenciales faltantes',
          `Por favor completa todas las credenciales requeridas:\n${missingCredentials.join('\n')}`
        );
        return false;
      }
      
      setCurrentStep(5);
      return true;
    }
    
    return true;
  };
  
  // Go back to previous step
  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };
  
  // Validate name and description
  const validateBasicInfo = () => {
    if (!formData.name.trim()) {
      showErrorAlert('Nombre requerido', 'Por favor ingresa un nombre para tu bot.');
      return false;
    }
    
    if (!formData.description.trim()) {
      showErrorAlert('Descripción requerida', 'Por favor ingresa una descripción para tu bot.');
      return false;
    }
    
    return true;
  };
  
  // Error alert helper
  const showErrorAlert = (title, message) => {
    Swal.fire({
      title: title,
      text: message,
      icon: 'error',
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#3182ce',
    });
  };
  
  // Success alert helper
  const showSuccessAlert = (title, message) => {
    Swal.fire({
      title: title,
      text: message,
      icon: 'success',
      confirmButtonText: 'Continuar',
      confirmButtonColor: '#3182ce',
    });
  };
  
  // Handle final submission
  const handleSubmit = async () => {
    if (!validateBasicInfo()) {
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Prepare provider IDs from all selected providers
      const allProviderIds = getAllSelectedProviderIds();
      
      // First create the provider connections
      const connectionsResponse = await apiService.createProviderConnections(formData.providerCredentials);
      
      if (connectionsResponse.status !== 'success') {
        throw new Error(connectionsResponse.message || 'Error creating provider connections');
      }
      
      // Extract the connection IDs from the response
      const connectionIds = connectionsResponse.connections.map(conn => conn.id);
      
      // Prepare data for Odoo API
      const botData = {
        name: formData.name,
        description: formData.description,
        system_prompt: formData.system_prompt || `Bot creado a partir del flujo ${formData.selectedFlux.name}`,
        provider_ids: allProviderIds,
        connection_ids: connectionIds,
        flux_id: formData.selectedFlux.id,
        process_attachments: true,
        make_calls: true,
        active: true,
        options: formData.options
      };
      
      // Call API to create bot
      const response = await apiService.createBot(botData);
      
      if (response.status === 'success') {
        // Add new bot to localStorage
        const currentBots = JSON.parse(localStorage.getItem('bots') || '[]');
        localStorage.setItem('bots', JSON.stringify([...currentBots, response.bot]));
        
        // Also update connections in localStorage
        const currentConnections = JSON.parse(localStorage.getItem('connections') || '[]');
        localStorage.setItem('connections', JSON.stringify([
          ...currentConnections, 
          ...connectionsResponse.connections
        ]));
        
        // Show success message
        showSuccessAlert(
          '¡Bot creado con éxito!', 
          'Tu nuevo bot basado en el flujo seleccionado ha sido creado correctamente. Serás redirigido al dashboard.'
        );
        
        // Navigate to dashboard
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        setError(response.message || 'Error al crear el bot');
      }
    } catch (error) {
      console.error('Failed to create bot:', error);
      setError(error.message || 'Error al crear el bot');
      
      // Show error message
      showErrorAlert(
        'Error al crear el bot',
        error.message || 'Ha ocurrido un error al crear el bot. Por favor, inténtalo de nuevo.'
      );
    } finally {
      setIsLoading(false);
    }
  };
  
  // Skip wizard and go to dashboard
  const handleSkip = () => {
    navigate('/dashboard');
  };
  
  // Provider card component
  const ProviderCard = ({ provider, isSelected, onClick }) => (
    <div 
      className={`provider-card ${isSelected ? 'selected' : ''} card-animate`}
      onClick={onClick}
    >
      {provider.image ? (
        <img src={`data:image/png;base64,${provider.image}`} alt={provider.name} />
      ) : (
        <div className="provider-placeholder">{provider.name.charAt(0)}</div>
      )}
      <h3>{provider.name}</h3>
      <p>{provider.description || provider.provider_type}</p>
      <div className="provider-selection">
        <FontAwesomeIcon 
          icon={isSelected ? faCheckCircle : faCircle} 
          className={isSelected ? 'pulse' : ''}
        />
      </div>
    </div>
  );
  
  // Flux card component
  const FluxCard = ({ flux, isSelected, onClick }) => (
    <div 
      className={`flux-card ${isSelected ? 'selected' : ''} card-animate`}
      onClick={onClick}
    >
      <h3>{flux.name}</h3>
      <p>{flux.description || 'No description available'}</p>
      <div className="flux-card-details">
        <span>
          {flux.input_provider_ids?.length || 0} Proveedores
        </span>
        <span>
          <FontAwesomeIcon 
            icon={isSelected ? faCheckCircle : faCircle} 
            className={isSelected ? 'pulse' : ''}
          />
        </span>
      </div>
    </div>
  );
  
  // Update getCredentialFieldsFromTemplate function to properly use the config_template
  const getCredentialFieldsFromTemplate = (provider) => {
    if (!provider) return [];
    
    try {
      // Try to get the config_template from the provider
      let template = {};
      
      if (provider.config_template) {
        // If it's a string, parse it
        if (typeof provider.config_template === 'string') {
          template = JSON.parse(provider.config_template);
        } else {
          // If it's already an object, use it directly
          template = provider.config_template;
        }
      } else {
        // Default templates based on provider type
        if (provider.provider_type === 'email' || provider.name.toLowerCase().includes('email') || provider.name.toLowerCase().includes('gmail')) {
          template = {
            "smtp_server": "smtp.gmail.com",
            "smtp_port": 587,
            "smtp_encryption": "tls",
            "smtp_user": "",
            "smtp_password": ""
          };
        } else if (provider.provider_type === 'social' || 
                  provider.name.toLowerCase().includes('whatsapp') || 
                  provider.name.toLowerCase().includes('telegram') ||
                  provider.name.toLowerCase().includes('facebook')) {
          template = {
            "api_key": "",
            "api_secret": "",
            "access_token": ""
          };
        } else {
          template = {
            "api_key": ""
          };
        }
      }
      
      // Convert template to field definitions
      const fields = [];
      Object.keys(template).forEach(key => {
        const value = template[key];
        let field = {
          key: key,
          label: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          required: true,
          default: value
        };
        
        if (key.includes('password') || key.includes('secret')) {
          field.type = 'password';
        } else if (key.includes('port')) {
          field.type = 'number';
        } else if (key.includes('encryption')) {
          field.type = 'select';
          field.options = ['tls', 'ssl', 'none'];
        } else {
          field.type = 'text';
        }
        
        fields.push(field);
      });
      
      return fields;
    } catch (error) {
      console.error('Error parsing config template for provider:', provider.name, error);
      // Return basic fields as fallback
      return [
        { key: 'api_key', label: 'API Key', type: 'text', required: true }
      ];
    }
  };
  
  // Prepare options section for the form based on the selected flux
  const renderOptionsSection = () => {
    if (!formData.selectedFlux || !formData.selectedFlux.options || formData.selectedFlux.options.length === 0) {
      return <div className="no-options-message">No hay opciones configurables para este flujo.</div>;
    }

    return (
      <div className="options-container">
        <h3>Opciones de Configuración</h3>
        {formData.selectedFlux.options.map(option => (
          <div key={option.id} className="option-item">
            <label className={option.is_required ? 'required' : ''}>
              {option.name}
              {option.description && <span className="option-description" title={option.description}>ℹ️</span>}
            </label>
            
            {option.option_type === 'boolean' && (
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id={`option-${option.id}`}
                  checked={formData.options[option.id] === true}
                  onChange={e => handleOptionChange(option.id, e.target.checked)}
                />
                <label htmlFor={`option-${option.id}`} className="toggle-label"></label>
              </div>
            )}
            
            {option.option_type === 'text' && (
              <input
                type="text"
                value={formData.options[option.id] || option.default_value || ''}
                onChange={e => handleOptionChange(option.id, e.target.value)}
                placeholder={option.description || `Ingrese ${option.name}`}
                className="form-control"
              />
            )}
            
            {option.option_type === 'number' && (
              <input
                type="number"
                value={formData.options[option.id] || option.default_value || ''}
                onChange={e => handleOptionChange(option.id, e.target.value)}
                placeholder={option.description || `Ingrese ${option.name}`}
                className="form-control"
              />
            )}
            
            {option.option_type === 'selection' && option.selection_options && (
              <select
                value={formData.options[option.id] || option.default_value || ''}
                onChange={e => handleOptionChange(option.id, e.target.value)}
                className="form-control"
              >
                <option value="">Seleccione una opción</option>
                {option.selection_options.map((selOption, idx) => (
                  <option key={idx} value={selOption}>
                    {selOption}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>
    );
  };
  
  // Handle flux generation from AI Guide
  const handleFluxFromAI = (generatedFlux) => {
    // Add the generated flux to the list if it doesn't exist
    const fluxExists = fluxes.some(f => f.name === generatedFlux.name);
    
    if (!fluxExists) {
      const newFlux = {
        id: `ai-generated-${Date.now()}`,
        name: generatedFlux.name,
        description: generatedFlux.description,
        configuration: generatedFlux.configuration,
        is_ai_generated: true,
        // Add any other required fields
      };
      
      setFluxes(prev => [...prev, newFlux]);
      handleFluxSelection(newFlux);
    }
    
    setShowAIGuide(false);
  };
  
  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="welcome-step">
            <h2>Selecciona un Flujo</h2>
            <p className="welcome-description">
              Los flujos son plantillas predefinidas que determinan cómo funcionará tu bot. Selecciona uno para comenzar.
            </p>
            
            {/* Force AI Guide to always show for testing */}
            <div className="ai-guide-section">
              <AIGuide onFluxGenerated={handleFluxFromAI} />
            </div>
            
            {fluxes.length === 0 ? (
              <div className="no-results">
                <p>No hay flujos disponibles. Contacta al administrador para obtener acceso a los flujos.</p>
              </div>
            ) : (
              <div className="flux-grid">
                {fluxes.map(flux => (
                  <FluxCard 
                    key={flux.id}
                    flux={flux}
                    isSelected={formData.selectedFlux?.id === flux.id}
                    onClick={() => handleFluxSelection(flux)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      
      case 2:
        return (
          <div className="welcome-step">
            <h2>Configura los Proveedores</h2>
            <p className="welcome-description">
              Selecciona los proveedores que quieres utilizar para este flujo. Puedes elegir entre proveedores de entrada, acción y salida.
            </p>
            
            {/* Input Providers */}
            {formData.selectedFlux?.input_provider_ids?.length > 0 && (
              <div className="providers-section">
                <h3>Proveedores de Entrada</h3>
                <p>Estos proveedores determinarán cómo los usuarios podrán interactuar con tu bot.</p>
                
                <div className="providers-grid">
                  {formData.selectedFlux.input_provider_ids.map(provider => {
                    const providerData = providers.find(p => p.id === provider.id);
                    if (!providerData) return null;
                    
                    return (
                      <ProviderCard
                        key={providerData.id}
                        provider={providerData}
                        isSelected={formData.selectedInputProviders.includes(providerData.id)}
                        onClick={() => handleProviderSelection(providerData.id, 'Input')}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Action Providers - Grouped by provider_action */}
            {formData.selectedFlux?.action_provider_ids?.length > 0 && (
              <div className="providers-section">
                <h3>Proveedores de Acción</h3>
                <p>Selecciona como máximo un proveedor para cada tipo de acción o elige "No ejecutar esta acción".</p>
                
                {Object.entries(groupActionProviders(formData.selectedFlux.action_provider_ids)).map(([actionType, providers]) => (
                  <div key={actionType} className="action-type-group">
                    <h4>{actionType}</h4>
                    
                    <div className="action-provider-options">
                      {/* "No ejecutar esta acción" option */}
                      <div 
                        className={`provider-card ${formData.selectedActionProviders[actionType] === null ? 'selected' : ''}`}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            selectedActionProviders: {
                              ...prev.selectedActionProviders,
                              [actionType]: null
                            }
                          }));
                        }}
                      >
                        <div className="provider-icon">
                          <FontAwesomeIcon 
                            icon={formData.selectedActionProviders[actionType] === null ? faCheckCircle : faCircle} 
                            size="lg"
                            className={formData.selectedActionProviders[actionType] === null ? 'selected-icon' : ''}
                          />
                        </div>
                        <div className="provider-info">
                          <h4>No ejecutar esta acción</h4>
                          <p>No se realizará ninguna acción de este tipo</p>
                        </div>
                      </div>
                      
                      {/* Provider options */}
                      {providers.map(provider => (
                        <ProviderCard
                          key={provider.id}
                          provider={provider}
                          isSelected={formData.selectedActionProviders[actionType] === provider.id}
                          onClick={() => handleProviderSelection(provider.id, 'Action')}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Output Providers */}
            {formData.selectedFlux?.output_provider_ids?.length > 0 && (
              <div className="providers-section">
                <h3>Proveedores de Salida</h3>
                <p>Estos proveedores determinarán cómo tu bot comunicará los resultados.</p>
                
                <div className="providers-grid">
                  {formData.selectedFlux.output_provider_ids.map(provider => {
                    const providerData = providers.find(p => p.id === provider.id);
                    if (!providerData) return null;
                    
                    return (
                      <ProviderCard
                        key={providerData.id}
                        provider={providerData}
                        isSelected={formData.selectedOutputProviders.includes(providerData.id)}
                        onClick={() => handleProviderSelection(providerData.id, 'Output')}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      
      case 3:
        return (
          <div className="welcome-step">
            <h2>Personaliza tu Bot</h2>
            <p className="welcome-description">
              Personaliza el nombre y la descripción de tu bot basado en el flujo seleccionado.
            </p>
            
            <div className="form-group">
              <label htmlFor="name">Nombre del Bot</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ej: Bot de Atención al Cliente"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Descripción</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe el propósito de este bot"
                rows={4}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="system_prompt">Prompt del Sistema (opcional)</label>
              <textarea
                id="system_prompt"
                name="system_prompt"
                value={formData.system_prompt}
                  onChange={handleInputChange}
                placeholder="Instrucciones específicas para el comportamiento del bot"
                rows={6}
              />
            </div>
            
            {/* Add options section here */}
            {renderOptionsSection()}
          </div>
        );
      
      case 4:
        return (
          <div className="welcome-step">
            <h2>Configura las credenciales de los proveedores</h2>
            <p className="welcome-description">
              Introduce las credenciales necesarias para cada proveedor seleccionado.
            </p>
            
            {/* Provider debug info - only in development */}
            {process.env.NODE_ENV === 'development' && (
              <div className="debug-info" style={{ margin: '15px 0', padding: '10px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px' }}>
                <details>
                  <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Debug: Provider Data</summary>
                  <pre style={{ maxHeight: '200px', overflow: 'auto', margin: '10px 0' }}>
                    {JSON.stringify(providers.filter(p => getAllSelectedProviderIds().includes(p.id)), null, 2)}
                  </pre>
                </details>
              </div>
            )}
            
            <div className="credentials-container">
              {getAllSelectedProviderIds().map(providerId => {
                const provider = providers.find(p => p.id === providerId);
                if (!provider) return null;
                
                // Get credential fields from template or defaults
                const credentialFields = getCredentialFieldsFromTemplate(provider);
                
                return (
                  <div key={providerId} className="provider-credentials-card">
                    <h3>
                      {provider.name}
                      {provider.image && (
                        <img 
                          src={`data:image/png;base64,${provider.image}`} 
                          alt={provider.name} 
                          style={{ width: '24px', height: '24px', marginLeft: '8px', verticalAlign: 'middle' }}
                        />
                      )}
                    </h3>
                    <div className="provider-type">
                      Tipo: {provider.provider_type || 'No especificado'}
                    </div>
                    
                    <div className="credentials-form">
                      {credentialFields.length > 0 ? (
                        credentialFields.map(field => (
                          <div key={field.key} className="form-group">
                            <label>
                              {field.label} {field.required && <span className="required">*</span>}
                            </label>
                            {field.type === 'select' ? (
                              <select
                                value={formData.providerCredentials[providerId]?.[field.key] || field.default || ''}
                                onChange={(e) => handleCredentialChange(providerId, field.key, e.target.value)}
                                required={field.required}
                              >
                                <option value="">Seleccionar {field.label}</option>
                                {field.options?.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                )) || <option value="">No options available</option>}
                              </select>
                            ) : (
                              <input
                                type={field.type}
                                value={formData.providerCredentials[providerId]?.[field.key] || ''}
                                onChange={(e) => handleCredentialChange(providerId, field.key, e.target.value)}
                                required={field.required}
                                placeholder={field.default || ''}
                              />
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="no-fields-message">
                          No se encontraron campos de configuración para este proveedor.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {getAllSelectedProviderIds().length === 0 && (
              <div className="no-providers-message" style={{ textAlign: 'center', margin: '30px 0' }}>
                <p>No has seleccionado ningún proveedor. Por favor regresa al paso anterior para seleccionar proveedores.</p>
                <button 
                  className="btn-secondary" 
                  onClick={() => setCurrentStep(3)}
                  style={{ margin: '15px auto', display: 'block' }}
                >
                  Volver a Selección de Proveedores
                </button>
              </div>
            )}
            
            <div className="wizard-navigation">
              <button className="btn-secondary" onClick={() => setCurrentStep(3)}>Atrás</button>
              <button className="btn-primary" onClick={nextStep}>Siguiente</button>
            </div>
          </div>
        );
      
      case 5:
        return (
          <div className="welcome-step">
            <h2>Listo para crear tu bot</h2>
            <p className="welcome-description">
              Revisa los detalles y crea tu bot.
            </p>
            
            <div className="summary-container">
              <div className="summary-section">
                <h3>Información General</h3>
                <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-label">Nombre:</span>
                    <span className="summary-value">{formData.name}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Descripción:</span>
                    <span className="summary-value">{formData.description}</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Flujo:</span>
                    <span className="summary-value">{formData.selectedFlux?.name}</span>
                  </div>
                </div>
              </div>
              
              <div className="summary-section">
                <h3>Proveedores Seleccionados</h3>
                <div className="providers-summary">
                  {formData.selectedInputProviders.length > 0 && (
                    <div className="provider-category">
                      <h4>Entrada</h4>
                      <ul>
                        {formData.selectedInputProviders.map(id => {
                          const provider = providers.find(p => p.id === id);
                          return provider ? (
                            <li key={id}>{provider.name}</li>
                          ) : null;
                        })}
                      </ul>
                    </div>
                  )}
                  
                  {formData.selectedActionProviders.length > 0 && (
                    <div className="provider-category">
                      <h4>Acción</h4>
                      <ul>
                        {Object.entries(formData.selectedActionProviders).map(([actionType, providerId]) => {
                          const provider = providers.find(p => p.id === providerId);
                          return provider ? (
                            <li key={`${actionType}-${providerId}`}>{provider.name} ({actionType})</li>
                          ) : null;
                        })}
                      </ul>
                    </div>
                  )}
                  
                  {formData.selectedOutputProviders.length > 0 && (
                    <div className="provider-category">
                      <h4>Salida</h4>
                      <ul>
                        {formData.selectedOutputProviders.map(id => {
                          const provider = providers.find(p => p.id === id);
                          return provider ? (
                            <li key={id}>{provider.name}</li>
                          ) : null;
                        })}
                      </ul>
                </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="wizard-navigation">
              <button className="btn-secondary" onClick={() => setCurrentStep(4)}>Atrás</button>
              <button className="btn-primary" onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? 'Creando...' : 'Crear Bot'}
              </button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Progress indicator component
  const ProgressIndicator = () => {
    const steps = [
      { label: 'Flujo', number: 1 },
      { label: 'Proveedores', number: 2 },
      { label: 'Datos', number: 3 },
      { label: 'Credenciales', number: 4 },
      { label: 'Crear', number: 5 }
    ];
    
    return (
      <div className="progress-indicator">
        {steps.map(step => (
          <div 
            key={step.number}
            className={`progress-step ${
              step.number === currentStep
                ? 'active'
                : step.number < currentStep
                ? 'completed'
                : ''
            }`}
          >
            <div className="step-number">{step.number}</div>
            <span>{step.label}</span>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="welcome-container">
      <div className="welcome-content">
        <div className="welcome-header card-animate">
          <h1>¡Crea tu Bot en Minutos!</h1>
          <p>Sigue estos sencillos pasos para configurar tu bot personalizado.</p>
        </div>
        
        <ProgressIndicator />
        
        <div className="welcome-step card-animate">
          {renderStep()}
          
          <div className="step-actions">
            {currentStep > 1 && (
              <button className="back-button" onClick={prevStep}>
                Anterior
              </button>
            )}
            
            {currentStep < 4 ? (
              <button className="continue-button" onClick={nextStep}>
                Continuar
              </button>
            ) : (
              <>
                <button className="skip-button" onClick={handleSkip}>
                  Omitir
                </button>
                <button 
                  className="submit-button pulse" 
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? 'Creando...' : 'Crear Bot'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Container = styled.div`
  /* Provider credentials styling */
  .credentials-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
  }

  .provider-credentials-card {
    background-color: var(--bg-card, #212121);
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: var(--monday-card-shadow, 0 4px 6px rgba(0, 0, 0, 0.1));
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    border: 1px solid var(--border-color, #333);
  }

  .provider-credentials-card:hover {
    transform: translateY(-5px);
    box-shadow: var(--monday-card-hover-shadow, 0 10px 15px rgba(0, 0, 0, 0.15));
  }

  .provider-credentials-card h3 {
    font-size: 1.2rem;
    margin-top: 0;
    margin-bottom: 1rem;
    color: var(--text-primary, #f0f0f0);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .credentials-form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .credentials-form .form-group {
    margin-bottom: 0;
  }

  .credentials-form .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--text-primary, #f7fafc);
    font-weight: 500;
    font-size: 0.95rem;
  }

  .credentials-form .form-group input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color, #4a5568);
    border-radius: 6px;
    background-color: var(--input-bg, #2d3748);
    color: var(--text-primary, #f7fafc);
    font-size: 1rem;
    transition: all 0.3s ease;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .credentials-form .form-group input:focus {
    outline: none;
    border-color: var(--primary-color, #3182ce);
    box-shadow: 0 0 0 2px rgba(49, 130, 206, 0.25), inset 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .credentials-form .form-text {
    color: var(--text-secondary, #b0b0b0);
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }

  /* Summary section styling */
  .summary-container {
    background-color: var(--bg-card, #212121);
    border-radius: 8px;
    box-shadow: var(--monday-card-shadow, 0 4px 6px rgba(0, 0, 0, 0.1));
    padding: 1.5rem;
    margin-top: 2rem;
    border: 1px solid var(--border-color, #333);
  }

  .summary-container h3 {
    font-size: 1.2rem;
    margin-top: 0;
    margin-bottom: 1rem;
    color: var(--text-primary, #f0f0f0);
  }

  .summary-section {
    padding: 1.5rem;
    border-radius: 8px;
    background-color: var(--bg-tertiary, #2a2a2a);
    margin-bottom: 1.5rem;
  }

  .summary-section:last-child {
    margin-bottom: 0;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .providers-summary {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .providers-summary-item {
    display: flex;
    justify-content: space-between;
    padding: 0.8rem;
    background-color: var(--bg-secondary, #1e293b);
    border-radius: 4px;
    color: var(--text-primary, #f0f0f0);
  }

  /* Wizard navigation styling */
  .wizard-navigation {
    display: flex;
    justify-content: space-between;
    margin-top: 2rem;
  }

  .wizard-navigation button {
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .wizard-navigation .back-button {
    background-color: transparent;
    color: var(--text-primary, #f7fafc);
    border: 1px solid var(--border-color, #4a5568);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .wizard-navigation .back-button::before {
    content: '←';
    transition: transform 0.2s ease;
  }

  .wizard-navigation .back-button:hover {
    background-color: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }

  .wizard-navigation .back-button:hover::before {
    transform: translateX(-4px);
  }

  .wizard-navigation .continue-button {
    background-color: var(--primary-color, #3182ce);
    color: white;
    border: none;
    box-shadow: 0 4px 6px rgba(49, 130, 206, 0.2);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .wizard-navigation .continue-button::after {
    content: '→';
    transition: transform 0.2s ease;
  }

  .wizard-navigation .continue-button:hover:not(:disabled) {
    background-color: var(--primary-color-dark, #2c5282);
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(49, 130, 206, 0.25);
  }

  .wizard-navigation .continue-button:hover::after {
    transform: translateX(4px);
  }

  .wizard-navigation .continue-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .credentials-container {
      grid-template-columns: 1fr;
    }
    
    .summary-grid {
      grid-template-columns: 1fr;
    }
  }

  .ai-guide-section {
    margin-bottom: 24px;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 24px;
  }
  
  .ai-guide-button {
    background-color: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    padding: 10px 16px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background-color 0.3s ease;
    
    &:hover {
      background-color: var(--primary-hover-color);
    }
    
    &:before {
      content: "✨";
    }
  }
`;

export default Welcome;